import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, X, Users } from "lucide-react";
import { api } from "@/lib/api";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { JaritBadge } from "@/components/JaritBadge";
import { useQuickProfile } from "@/context/QuickProfileContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Global employee quick search. Uses the permission-safe /employees list
// (daily rate is nulled server-side for non-admins) and opens the Quick Profile popup.
// NOTE: the input is NOT auto-focused — on mobile that would pop the keyboard
// immediately, which the user dislikes. User taps the field when ready.
export function GlobalSearchDialog({ open, onOpenChange }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState("");
  const { openQuickProfile } = useQuickProfile();

  useEffect(() => {
    if (!open) return;
    setQ("");
    if (all === null) {
      api.get("/employees").then((r) => setAll(r.data)).catch(() => setAll([]));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = all || [];
    if (!term) return list.slice(0, 20);
    return list
      .filter(
        (e) =>
          (e.nama || "").toLowerCase().includes(term) ||
          (e.emp_code || "").toLowerCase().includes(term)
      )
      .slice(0, 30);
  }, [q, all]);

  const pick = (id) => {
    onOpenChange(false);
    openQuickProfile(id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="global-search-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="gap-0 overflow-hidden rounded-[22px] border border-white/50 bg-white/95 p-0 shadow-[0_24px_70px_rgba(0,0,0,0.22)] backdrop-blur-2xl sm:max-w-[540px]"
      >
        <DialogHeader className="px-4 pb-0 pt-4 text-left">
          <DialogTitle className="text-[16px] font-semibold tracking-tight">Search Employees</DialogTitle>
        </DialogHeader>

        {/* Search bar */}
        <div className="px-4 pb-3 pt-3">
          <div className="flex items-center gap-2.5 rounded-2xl border border-[#E1E1E6] bg-[#F5F5F7] px-3.5 py-3 transition-colors focus-within:border-[#0071E3] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#0071E3]/20">
            <Search className="h-[18px] w-[18px] shrink-0 text-[#8E8E93]" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search employees — name or NIP"
              className="h-6 w-full bg-transparent text-[15px] outline-none placeholder:text-[#B0B0B8]"
            />
            {q && (
              <button data-testid="global-search-clear" onClick={() => setQ("")} className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-black/[0.06] text-[#6E6E73] transition-colors hover:bg-black/[0.1]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[62vh] overflow-y-auto px-2 pb-2">
          <div className="flex items-center justify-between px-2.5 pb-1.5 pt-0.5">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#A1A1A6]">
              <Users className="h-3.5 w-3.5" /> {q.trim() ? "Results" : "Employees"}
            </span>
            <span className="text-[11px] font-medium text-[#B0B0B8]">{results.length}</span>
          </div>
          {all === null ? (
            <div className="flex items-center justify-center gap-2 py-12 text-[13px] text-[#8E8E93]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : results.length === 0 ? (
            <p data-testid="global-search-empty" className="py-12 text-center text-[13.5px] text-[#8E8E93]">
              No employees found.
            </p>
          ) : (
            results.map((e) => (
              <button
                key={e.id}
                data-testid={`global-search-result-${e.id}`}
                onClick={() => pick(e.id)}
                className="flex w-full items-center gap-3 rounded-2xl px-2.5 py-2.5 text-left transition-colors hover:bg-[#F2F5FA] active:bg-[#EAF0F8]"
              >
                <EmployeeAvatar id={e.id} nama={e.nama} hasPhoto={!!(e.has_photo || e.photo_path)} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate text-[14.5px] font-medium text-[#1D1D1F]">
                    <span className="truncate">{e.nama}</span>
                    {e.verified && !e.is_jarit && <VerifiedBadge size={15} className="shrink-0" title="Verified employee" />}
                    <JaritBadge show={e.is_jarit} size={16} />
                  </p>
                  <p className="num truncate text-[12px] text-[#8E8E93]">
                    {e.emp_code || "—"} · {e.jabatan || "—"}
                  </p>
                </div>
                {!e.aktif && <span className="shrink-0 rounded-full bg-[#F2F2F7] px-2 py-0.5 text-[10.5px] font-medium text-[#8E8E93]">Inactive</span>}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalSearchDialog;
